import React, { useCallback, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormHelperText } from "@mui/material";
import { toast } from "react-toastify";
import { Task } from "../types";
import { SideOverlay, BoothTree } from "../../../components";
import { useStore } from "../../../helpers";
import { safe_get } from "../../../report-visuals";
import {
    FTPAutoComplete,
    FTPFormButton,
    FTPInput,
    FTPSelect,
} from "../../../components-new";
import { taskDrawerSchema, TaskDrawerFormValues } from "./taskEditSchema";
import { useTaskMutations } from "../hooks/useTaskMutations";

interface TaskEditProps {
    isOpen: boolean;
    onClose: () => void;
    task: Task | null;
    onCancel: () => void;
    onRefresh?: () => void;
}

export const TaskEdit: React.FC<TaskEditProps> = observer(
    ({ isOpen, onClose, task, onCancel, onRefresh }) => {
        const { taskManagerStore } = useStore();
        const { users, surveys } = taskManagerStore;

        // Mutations
        const { updateTaskMutation } = useTaskMutations({
            taskId: task?.id,
            onRefresh,
            onClose, // Close drawer after successful update (don't open view drawer)
        });

        // Form default values
        const defaultValues = useMemo<TaskDrawerFormValues>(() => {
            return {
                name: safe_get(task, "more.name", ""),
                surveyid: task?.surveyid ? String(task.surveyid) : "",
                count: String(safe_get(task, "count.count", "")),
                node: task?.node || {},
                user: task?.user || null,
            };
        }, [task]);

        const surveyOptions = useMemo(
            () =>
                (surveys || []).map((s: any) => ({
                    value: String(s.id),
                    label: s.name,
                })),
            [surveys]
        );

        const handleSubmit = useCallback(
            (values: TaskDrawerFormValues) => {
                if (!task) return;
                const parsedSurveyId = parseInt(values.surveyid, 10);
                const parsedCount = parseInt(values.count, 10);

                const updatedTask = {
                    ...task,
                    more: { ...task.more, name: values.name },
                    count: { count: parsedCount },
                    surveyid: parsedSurveyId,
                    node: values.node,
                    user: values.user,
                    userid: values.user?.id || -1,
                };
                updateTaskMutation.mutate(updatedTask);
            },
            [task, updateTaskMutation]
        );

        // Form setup
        const methods = useForm<TaskDrawerFormValues>({
            defaultValues,
            resolver: yupResolver(taskDrawerSchema) as any,
            mode: "onBlur",
        });

        const {
            handleSubmit: formHandleSubmit,
            reset,
            clearErrors,
            formState: { errors },
        } = methods;

        useEffect(() => {
            reset(defaultValues);
        }, [defaultValues, reset]);

        const submit = formHandleSubmit(
            (values) => handleSubmit(values),
            (invalid) => {
                const first = Object.values(invalid || {})[0] as any;
                const msg = first?.message;
                if (msg) toast.error(msg);
            }
        );

        if (!task) {
            return (
                <SideOverlay
                    show={isOpen}
                    onClose={onClose}
                    title="Edit Task"
                    size="medium"
                >
                    <div className="p-6 text-sm text-slate-500">
                        No task selected.
                    </div>
                </SideOverlay>
            );
        }

        return (
            <SideOverlay
                show={isOpen}
                onClose={onClose}
                title="Edit Task"
                size="medium"
            >
                <FormProvider {...methods}>
                    <form
                        onSubmit={submit}
                        className="flex flex-col h-full bg-white rounded-lg"
                    >
                        <div className="p-4 space-y-4">
                            {/* Task Name */}
                            <FTPInput
                                name="name"
                                label="Task Name"
                                placeholder="Enter task name"
                            />

                            {/* Survey */}
                            <FTPSelect
                                name="surveyid"
                                label="Survey *"
                                options={surveyOptions}
                            />

                            {/* Interview Count */}
                            <FTPInput
                                name="count"
                                label="Interviews per Booth *"
                                numericOnly
                                placeholder="Enter number of interviews"
                            />

                            {/* Booth Selection */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    <i className="fas fa-location-dot text-xs mr-2 text-slate-400" />
                                    Select Booth
                                </label>

                                <Controller
                                    name="node"
                                    render={({ field }) => (
                                        <>
                                            <BoothTree
                                                onBoothSelection={(
                                                    data: any
                                                ) => {
                                                    field.onChange(data);
                                                    clearErrors("node");
                                                }}
                                                selected_booth={safe_get(
                                                    field.value,
                                                    "id",
                                                    ""
                                                )}
                                            />

                                            {errors.node?.message && (
                                                <FormHelperText error>
                                                    {String(
                                                        errors.node.message
                                                    )}
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                />
                            </div>

                            {/* Assign To */}
                            <FTPAutoComplete
                                name="user"
                                label="Assign To"
                                data={users}
                                lookupKey="name"
                                placeholder="Search and assign user..."
                                addNewOption={false}
                            />
                        </div>

                        <div className="mt-auto px-6 pb-6">
                            <div className="pt-4 border-t border-slate-200 flex justify-end flex-row gap-3">
                                <FTPFormButton
                                    actionType="Cancel"
                                    handleButtonClick={onCancel}
                                    type="button"
                                />
                                <FTPFormButton
                                    actionType="Save"
                                    handleButtonClick={() => {}}
                                    type="submit"
                                />
                                {updateTaskMutation.isLoading && (
                                    <span className="ml-2 text-sm text-slate-500 flex items-center gap-2">
                                        <i className="fas fa-spinner fa-spin" />
                                        Saving...
                                    </span>
                                )}
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </SideOverlay>
        );
    }
);
